import { useMemo } from 'react'
import { Select, type SelectOption } from '@/components/ui/Select'
import { useStore } from '@/stores'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import type { AssetId } from '@/types'

export function AssetSelect() {
  const { assets, status, error, lastLoadedFromCache } = useTaxonomyStore((state) => ({
    assets: state.data ? [...state.data.assets].sort((a, b) => a.order - b.order) : [],
    status: state.status,
    error: state.error,
    lastLoadedFromCache: state.lastLoadedFromCache,
  }))
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const setAsset = useStore((state) => state.setAsset)

  const options = useMemo<SelectOption<AssetId>[]>(
    () => assets.map((asset) => ({ id: asset.id, label: asset.label })),
    [assets],
  )

  if (options.length === 0) {
    if (status === 'error') {
      return (
        <p className="text-xs text-red-400">
          {error || 'Unable to load asset list. Check your connection.'}
        </p>
      )
    }
    return (
      <p className="text-xs text-gray-400">
        {status === 'loading' ? 'Loading assets…' : 'No assets available'}
      </p>
    )
  }

  const selectedAsset = assets.find((asset) => asset.id === selectedAssetId)
  const triggerLabel = selectedAsset ? selectedAsset.label : 'Asset'

  return (
    <div className="space-y-2">
      <Select<AssetId>
        value={selectedAssetId}
        onChange={setAsset}
        options={options}
        triggerLabel={triggerLabel}
        ariaLabel="Select asset"
        className="asset-select"
      />
      {lastLoadedFromCache && status === 'ready' && (
        <p className="text-xs text-gray-400">Using cached taxonomy while offline.</p>
      )}
    </div>
  )
}
